import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import Loader from "Components/Loader"
import UsersSupport from "Components/SupportViews/UsersSupport"

import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Divider,
    Chip,
} from "@mui/material"

//Iconos
import AppControlIcon from 'assets/images/Administration/control-app.png'
import AuthorizationIcon from 'assets/images/Administration/order-processed.png'
import RoleControlIcon from 'assets/images/Administration/role-control.png'
import AccountControl from 'assets/images/Administration/id-card.png'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { useAuth } from "Context/Auth/useAuth"

const UserControl = () => {
    const { t } = useTranslation()
    const auth = useAuth()
    const [itemSelected, setItemSelected] = useState("")

    const elementsValidateRender = useCallback((permiss: string[]): any[] => {
        let list = [
            {
                title: t("userControl.text_1"),
                url: "users",
                type: "userReview",
                icon: AccountControl,
                description: "Gestión de usuarios del sistema"
            }, {
                title: t("userControl.text_2"),
                url: "roleGestion",
                type: "roleManagement",
                icon: RoleControlIcon,
                description: "Administración de roles y permisos"
            }, {
                title: t("userControl.text_3"),
                url: "authorizationOrder",
                type: "orderAuthorization",
                icon: AuthorizationIcon,
                description: "Autorización de órdenes pendientes"
            }, {
                title: t("userControl.text_4"),
                url: "appControl",
                type: "controlApp",
                icon: AppControlIcon,
                description: "Control de la aplicación"
            }
        ]
        list = list.filter((item: any) => permiss.includes(item.type));
        return list
        //eslint-disable-next-line
    }, [])

    const userList: any[] = useMemo(() => {
        if (auth.user?.role === "admin" || auth.user?.role === "adminsupport") {
            setItemSelected("users")
            return elementsValidateRender([
                "userReview",
                "roleManagement",
                "orderAuthorization",
            ])
        } else if (auth.user?.role === "adminComercial") {
            setItemSelected("users")
            return elementsValidateRender([
                "userReview",
            ])
        } else if (auth.user?.role === "displayerSupervisor") {
            setItemSelected("users")
            return elementsValidateRender([
                "userReview",
                "roleManagement",
                "orderAuthorization",
                "controlApp"
            ])
        }
        return []
    }, [auth.user?.role, elementsValidateRender])

    return (
        <>
            {
                auth.user?.role !== "" ?
                    <Box sx={{
                        backgroundColor: "#f5f5f5",
                        // minHeight: "calc(100vh - 164px)",
                        maxHeight: "calc(100vh - 1000px)",
                        p: 3
                    }}>
                        <Box sx={{ maxWidth: "1600px", margin: "0 auto" }}>
                            {/* Header */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 3,
                            }}>
                                <Avatar sx={{
                                    bgcolor: '#173456',
                                    width: 56,
                                    height: 56
                                }}>
                                    <AdminPanelSettingsIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
                                        Panel de Administración
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Gestiona usuarios, roles y permisos del sistema
                                    </Typography>
                                </Box>
                                <Chip
                                    label={auth.user?.role}
                                    color="primary"
                                    sx={{ ml: 'auto', fontWeight: 600 }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                {/* Menú lateral */}
                                <Paper
                                    elevation={2}
                                    sx={{
                                        width: '300px',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        height: 'fit-content',
                                        position: 'sticky',
                                        top: 80
                                    }}
                                >
                                    <List sx={{ p: 0 }}>
                                        {userList?.map((item: any, i: number) => {
                                            const isSelected = itemSelected === item.url;
                                            return (
                                                <Box key={i}>
                                                    <ListItem disablePadding>
                                                        <ListItemButton
                                                            selected={isSelected}
                                                            onClick={() => setItemSelected(item.url)}
                                                            sx={{
                                                                py: 2,
                                                                px: 2,
                                                                transition: 'all 0.3s ease',
                                                                '&.Mui-selected': {
                                                                    backgroundColor: '#e3f2fd',
                                                                    borderLeft: '4px solid #173456',
                                                                    '& .MuiListItemIcon-root': {
                                                                        transform: 'scale(1.1)',
                                                                    },
                                                                    '& .MuiListItemText-primary': {
                                                                        color: '#173456',
                                                                        fontWeight: 700,
                                                                    }
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: '#f5f5f5',
                                                                    '& .MuiListItemIcon-root': {
                                                                        transform: 'scale(1.05)',
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{
                                                                minWidth: 56,
                                                                transition: 'transform 0.3s ease'
                                                            }}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 44,
                                                                        height: 44,
                                                                        backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={item.icon}
                                                                        alt={item.title}
                                                                        style={{
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            objectFit: 'contain'
                                                                        }}
                                                                    />
                                                                </Avatar>
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={item.title}
                                                                secondary={item.description}
                                                                primaryTypographyProps={{
                                                                    fontSize: '0.95rem',
                                                                    fontWeight: isSelected ? 600 : 500
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    fontSize: '0.75rem',
                                                                    sx: { mt: 0.5 }
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    {i < userList.length - 1 && <Divider />}
                                                </Box>
                                            )
                                        })}
                                    </List>
                                </Paper>

                                {/* Contenido principal */}
                                <Box sx={{ flex: 1 }}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 3,
                                            borderRadius: 2,
                                            minHeight: '600px',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        {itemSelected === "users" && <UsersSupport />}
                                        {itemSelected === "roleGestion" && (
                                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                                <Typography variant="h5" color="text.secondary">
                                                    Gestión de Roles
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Esta sección está en desarrollo
                                                </Typography>
                                            </Box>
                                        )}
                                        {itemSelected === "authorizationOrder" && (
                                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                                <Typography variant="h5" color="text.secondary">
                                                    Autorización de Órdenes
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Esta sección está en desarrollo
                                                </Typography>
                                            </Box>
                                        )}
                                        {itemSelected === "appControl" && (
                                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                                <Typography variant="h5" color="text.secondary">
                                                    Control de Aplicación
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Esta sección está en desarrollo
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Loader
                        inPage={true}
                        openLoader={true}
                        redirect={false}
                        redirectHome={false}
                        quantity={0} />
            }
        </>
    )
}

export default UserControl;
